import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePlanContext } from "../../../classes/contexts/PlanContext";
import { ButtonSize, IconSize } from "../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../classes/enums/IconsEnum";
import {
  ActNatures,
  SpineRigidityType,
} from "../../../classes/enums/SurgicalActsEnum";
import Button from "../../../components/button/Button";
import IconButton from "../../../components/iconButton/IconButton";
import IncrementDecrementButtons from "../../../components/incrementDecrementButtons/IncrementDecrementButtons";
import MeasurementDisplay from "../../../components/measurementDisplay/MeasurementDisplay";
import RadioButtons from "../../../components/radioButtons/RadioButtons";
import LabeledRangeSelect from "../../../components/select/LabeledRangeSelect";
import LabeledSelect from "../../../components/select/LabeledSelect";
import { TooltipOrientation } from "../../../components/tooltip/Tooltip";
import useTranslation from "../../../hooks/i18n/useTranslation";
import useSurgicalActActions from "../../../hooks/useSurgicalActActions";
import "./AlignPopupParameterSelector.css";

const Rod = ({
  rods = [],
  onAddRod = () => {},
  onUpdateRod = () => {},
  onRemoveRod = () => {},
  onConfirm = null,
  screwSpecs = {},
  vertebraRangeSelection = {},
  canApplyReciprocalChange = false,
  selectedSpineType = SpineRigidityType.RIGID,
  onSpineTypeChange = () => {},
}) => {
  const { t } = useTranslation();
  const { currentPlanId } = usePlanContext();
  const { removeRodPreview, showRodHardware } =
    useSurgicalActActions(currentPlanId);

  const [isConfirming, setIsConfirming] = useState(false);
  const [isLoadingRodTool] = useState(false);
  const [expandedRodIds, setExpandedRodIds] = useState(new Set());
  const isConfirmingRef = useRef(false);

  // Get vertebra selection utilities from shared hook
  const {
    handleFirstVertebraChange,
    handleSecondVertebraChange,
    getRangeForRod,
    getSelectedVertebraItem,
    getLastSelectedVertebraItem,
    getFirstDropdownItems,
    getSecondDropdownItems,
    getSideAvailability,
  } = vertebraRangeSelection;

  const diameterItems = useMemo(
    () => [
      { value: 4.5, label: "4.5 mm" },
      { value: 5.5, label: "5.5 mm" },
      { value: 6.0, label: "6.0 mm" },
      { value: 6.25, label: "6.25 mm" },
      { value: 6.5, label: "6.5 mm" },
      { value: 7.0, label: "7.0 mm" },
    ],
    []
  );

  const materialItems = useMemo(
    () => [
      { value: "Titanium", label: "Titanium" },
      { value: "Titanium Alloy", label: "Titanium Alloy" },
      { value: "Cobalt Chrome", label: "Cobalt Chrome" },
    ],
    []
  );

  const getValidLeftSelection = useCallback(
    rod => (rod.selectedLeftScrews || []).filter(v => v && v !== "-"),
    []
  );

  const getValidRightSelection = useCallback(
    rod => (rod.selectedRightScrews || []).filter(v => v && v !== "-"),
    []
  );

  const getSelectedDiameterItem = useCallback(
    rod =>
      diameterItems.find(item => item.value === rod.rodDiameter) ||
      diameterItems[2],
    [diameterItems]
  );

  useEffect(() => {
    if (
      !canApplyReciprocalChange &&
      selectedSpineType !== SpineRigidityType.RIGID
    ) {
      onSpineTypeChange(SpineRigidityType.RIGID);
    }
  }, [canApplyReciprocalChange, onSpineTypeChange, selectedSpineType]);

  const isRodExpanded = useCallback(
    rodId => expandedRodIds.has(rodId),
    [expandedRodIds]
  );

  const toggleRodExpanded = useCallback(rodId => {
    setExpandedRodIds(prev => {
      const next = new Set(prev);
      if (next.has(rodId)) {
        next.delete(rodId);
      } else {
        next.add(rodId);
      }
      return next;
    });
  }, []);

  const getRodLengthCalculated = useCallback(
    (rod, side = null) => {
      const screws =
        side === "left"
          ? rod.selectedLeftScrews || []
          : side === "right"
          ? rod.selectedRightScrews || []
          : getRangeForRod(rod);
      if (screws.length < 2) return "N/A";
      const baseLength = screws.length * 25;
      const extra = rod.overhangLength ?? 0;
      return `${baseLength + extra} mm`;
    },
    [getRangeForRod]
  );

  // Get rod characteristics label: "Characteristics - (upper-lower, L/R)"
  const getRodCharacteristicsLabel = useCallback(
    rod => {
      const range = getRangeForRod(rod);
      const hasLeft = (rod.selectedLeftScrews || []).length >= 2;
      const hasRight = (rod.selectedRightScrews || []).length >= 2;

      const upperVertebra = range.length > 0 ? range[0] : "-";
      const lowerVertebra = range.length > 1 ? range[range.length - 1] : "-";

      const sides = [];
      if (hasLeft) sides.push("L");
      if (hasRight) sides.push("R");
      const sidesStr = sides.length > 0 ? sides.join("/") : "-";

      return `Characteristics - (${upperVertebra}-${lowerVertebra}, ${sidesStr})`;
    },
    [getRangeForRod]
  );

  const getDefaultScrewSpec = useCallback(vertebraId => {
    // Use same logic as RodsSelector for consistency
    // This function provides default screw specifications to avoid timing issues
    // where Rod.js renders before RodsSelector has populated screwSpecs
    if (vertebraId === "C7") {
      return { diameter: 4.5, length: 30 };
    }

    const thoracicMatch = /^T(\d+)$/i.exec(vertebraId);
    if (thoracicMatch) {
      const level = Number(thoracicMatch[1]);
      if (level <= 5) {
        return { diameter: 4.5, length: 30 };
      }
      return { diameter: 5.5, length: 40 };
    }

    const lumbarMatch = /^L(\d+)$/i.exec(vertebraId);
    if (lumbarMatch) {
      return { diameter: 6.5, length: 45 };
    }

    if (vertebraId === "LSTV") {
      return { diameter: 6.5, length: 45 };
    }

    if (vertebraId === "S1") {
      return { diameter: 6.5, length: 40 };
    }

    if (vertebraId === "S2") {
      return { diameter: 7.5, length: 70 };
    }

    return { diameter: 6.5, length: 45 }; // fallback
  }, []);

  const screwSummary = useMemo(() => {
    const summaryMap = new Map();
    const appendSummary = selection => {
      selection.forEach(vertebraId => {
        // Use provided spec or fall back to default
        let spec = screwSpecs[vertebraId];
        if (!spec) {
          spec = getDefaultScrewSpec(vertebraId);
        }
        const label = `${spec.diameter} mm × ${spec.length} mm`;
        summaryMap.set(label, (summaryMap.get(label) || 0) + 1);
      });
    };

    rods.forEach(rod => {
      appendSummary(getValidLeftSelection(rod));
      appendSummary(getValidRightSelection(rod));
    });

    const result = Array.from(summaryMap.entries()).map(([label, count]) => ({
      label,
      count,
    }));
    return result;
  }, [
    rods,
    screwSpecs,
    getDefaultScrewSpec,
    getValidLeftSelection,
    getValidRightSelection,
  ]);

  useEffect(() => {
    return () => {
      (async () => {
        await removeRodPreview();

        if (!isConfirmingRef.current) {
          await showRodHardware();
        }
      })();
    };
  }, [removeRodPreview, showRodHardware]);

  const areScrewSpecsComplete = useMemo(() => {
    return true;
  }, []);

  const hasValidRod = useMemo(() => {
    return rods.some(rod => {
      const selectedVertebrae = getRangeForRod(rod);
      const validLeftSelection = getValidLeftSelection(rod);
      const validRightSelection = getValidRightSelection(rod);
      const hasValidVertebrae =
        selectedVertebrae.length >= 2 &&
        selectedVertebrae[0] &&
        selectedVertebrae[0] !== "-";
      const hasActiveRodSelection =
        validLeftSelection.length >= 2 || validRightSelection.length >= 2;
      const hasValidDiameter =
        rod.rodDiameter !== null && rod.rodDiameter !== undefined;
      const hasValidExtraLength =
        rod.overhangLength !== null && rod.overhangLength !== undefined;

      return (
        hasValidVertebrae &&
        hasActiveRodSelection &&
        hasValidDiameter &&
        hasValidExtraLength
      );
    });
  }, [getRangeForRod, getValidLeftSelection, getValidRightSelection, rods]);

  const isConfirmDisabled = useMemo(
    () =>
      !hasValidRod ||
      !areScrewSpecsComplete ||
      isLoadingRodTool ||
      isConfirming,
    [areScrewSpecsComplete, hasValidRod, isConfirming, isLoadingRodTool]
  );

  //region  Event handlers
  const handleLeftRodToggle = useCallback(
    rod => {
      const selectedVertebrae = getRangeForRod(rod);
      const isEnabled = (rod.selectedLeftScrews || []).length > 0;
      if (!isEnabled) {
        if (selectedVertebrae.length === 0) {
          return;
        }
        onUpdateRod(rod.id, {
          selectedLeftScrews: [...selectedVertebrae],
        });
      } else {
        onUpdateRod(rod.id, { selectedLeftScrews: [] });
      }
    },
    [getRangeForRod, onUpdateRod]
  );

  const handleRightRodToggle = useCallback(
    rod => {
      const selectedVertebrae = getRangeForRod(rod);
      const isEnabled = (rod.selectedRightScrews || []).length > 0;
      if (!isEnabled) {
        if (selectedVertebrae.length === 0) {
          return;
        }
        onUpdateRod(rod.id, {
          selectedRightScrews: [...selectedVertebrae],
        });
      } else {
        onUpdateRod(rod.id, { selectedRightScrews: [] });
      }
    },
    [getRangeForRod, onUpdateRod]
  );
  const handleDiameterChange = useCallback(
    (rodId, selectedItem) => {
      const value = selectedItem.target
        ? selectedItem.target.value
        : selectedItem.value;
      onUpdateRod(rodId, { rodDiameter: Number(value) });
    },
    [onUpdateRod]
  );

  const handleExtraLengthIncrement = useCallback(
    rod => {
      onUpdateRod(rod.id, prev => {
        const nextValue = Math.min((prev.overhangLength || 0) + 1, 20);
        return { ...prev, overhangLength: nextValue };
      });
    },
    [onUpdateRod]
  );

  const handleExtraLengthDecrement = useCallback(
    rod => {
      onUpdateRod(rod.id, prev => {
        const nextValue = Math.max((prev.overhangLength || 0) - 1, 0);
        return { ...prev, overhangLength: nextValue };
      });
    },
    [onUpdateRod]
  );

  const handleExtraLengthIncrementEnd = useCallback(() => {
    document.removeEventListener("mouseup", handleExtraLengthIncrementEnd);
    document.removeEventListener("mouseleave", handleExtraLengthIncrementEnd);
  }, []);

  const handleExtraLengthDecrementEnd = useCallback(() => {
    document.removeEventListener("mouseup", handleExtraLengthDecrementEnd);
    document.removeEventListener("mouseleave", handleExtraLengthDecrementEnd);
  }, []);

  const buildScrewPayload = useCallback(
    (selection, specs) => {
      return selection.reduce((acc, vertebraId) => {
        const spec = specs[vertebraId] || getDefaultScrewSpec(vertebraId);
        acc[vertebraId] = spec;
        return acc;
      }, {});
    },
    [getDefaultScrewSpec]
  );

  const getRepresentativeSpec = useCallback(
    (selection, specs) => {
      for (let i = 0; i < selection.length; i += 1) {
        const spec = specs[selection[i]];
        if (spec) {
          return spec;
        }
      }
      if (selection.length > 0) {
        return getDefaultScrewSpec(selection[0]);
      }
      return null;
    },
    [getDefaultScrewSpec]
  );

  const handleConfirm = useCallback(async () => {
    if (isConfirming) return;

    setIsConfirming(true);
    isConfirmingRef.current = true;

    try {
      const rodActs = [];

      rods.forEach(rod => {
        const validLeftSelection = getValidLeftSelection(rod);
        const validRightSelection = getValidRightSelection(rod);
        const rodCalculatedLength = getRodLengthCalculated(rod);

        if (validLeftSelection.length >= 2) {
          const leftScrewsPayload = buildScrewPayload(
            validLeftSelection,
            screwSpecs
          );
          const leftSpec = getRepresentativeSpec(
            validLeftSelection,
            screwSpecs
          );
          const leftRodAct = {
            actNature: ActNatures.Rod,
            vertebra: validLeftSelection[0],
            value: null,
            rodPairId: rod.rodPairId, // ID to pair left/right rods together
            rodVertebraeSubset: validLeftSelection,
            rodDiameter: rod.rodDiameter,
            side: "left",
            // Flat rod specifications
            rodType: rod.rodType,
            rodManufacturer: rod.rodManufacturer,
            rodProduct: rod.rodProduct,
            rodExtraLength: rod.overhangLength,
            rodMaterial: rod.rodMaterial,
            rodCalculatedLength: rodCalculatedLength,
            // Flat screw specifications
            screwType: "Pedicle",
            screwManufacturer: rod.screwManufacturer,
            screwProduct: rod.screwProduct,
            screwDiameter: leftSpec?.diameter ?? rod.screwDiameter,
            screwLength: leftSpec?.length ?? rod.screwLength,
            screwMaterial: rod.screwMaterial,
            screwCount: validLeftSelection.length * 2,
            screws: {
              L: leftScrewsPayload,
              R: {},
            },
          };
          rodActs.push(leftRodAct);
        }

        if (validRightSelection.length >= 2) {
          const rightScrewsPayload = buildScrewPayload(
            validRightSelection,
            screwSpecs
          );
          const rightSpec = getRepresentativeSpec(
            validRightSelection,
            screwSpecs
          );
          const rightRodAct = {
            actNature: ActNatures.Rod,
            vertebra: validRightSelection[0],
            value: null,
            rodPairId: rod.rodPairId, // ID to pair left/right rods together
            rodVertebraeSubset: validRightSelection,
            rodDiameter: rod.rodDiameter,
            side: "right",
            // Flat rod specifications
            rodType: rod.rodType,
            rodManufacturer: rod.rodManufacturer,
            rodProduct: rod.rodProduct,
            rodExtraLength: rod.overhangLength,
            rodMaterial: rod.rodMaterial,
            rodCalculatedLength: rodCalculatedLength,
            // Flat screw specifications
            screwType: "Pedicle",
            screwManufacturer: rod.screwManufacturer,
            screwProduct: rod.screwProduct,
            screwDiameter: rightSpec?.diameter ?? rod.screwDiameter,
            screwLength: rightSpec?.length ?? rod.screwLength,
            screwMaterial: rod.screwMaterial,
            screwCount: validRightSelection.length * 2,
            screws: {
              L: {},
              R: rightScrewsPayload,
            },
          };
          rodActs.push(rightRodAct);
        }
      });

      if (rodActs.length === 0) {
        console.warn("[Rod] No valid rod acts to confirm.");
        return;
      }

      if (
        canApplyReciprocalChange &&
        selectedSpineType !== SpineRigidityType.RIGID
      ) {
        try {
          const reciprocalChangeAct = {
            actNature: ActNatures.ReciprocalChange,
            spineRigidityType: selectedSpineType,
          };
          rodActs.push(reciprocalChangeAct);
        } catch (error) {
          console.error("[Rod] Failed to create reciprocal change act:", error);
        }
      }

      if (onConfirm) {
        await onConfirm(rodActs);
      }
    } catch (error) {
    } finally {
      setIsConfirming(false);
      isConfirmingRef.current = false;
    }
  }, [
    isConfirming,
    rods,
    onConfirm,
    buildScrewPayload,
    getRepresentativeSpec,
    getValidLeftSelection,
    getValidRightSelection,
    getRodLengthCalculated,
    screwSpecs,
    canApplyReciprocalChange,
    selectedSpineType,
  ]);

  //region Rendering

  const screwMaterialValue =
    rods[0]?.screwMaterial || materialItems[0]?.value || "Titanium";

  return (
    <div className="AlignPopupParameterSelector-container">
      <div className="AlignPopupParameterSelector-scrollable-content">
        {/* Generic Section */}
        <div className="AlignPopupParameterSelector-controls-wrapper">
          <label className="AlignPopupParameterSelector-label App-text-large">
            Vertebra
          </label>
          {rods.map((rod, index) => {
            const selectedRange = getRangeForRod(rod);
            const isLeftRodEnabled = (rod.selectedLeftScrews || []).length > 0;
            const isRightRodEnabled =
              (rod.selectedRightScrews || []).length > 0;
            const selectedVertebraItem = getSelectedVertebraItem(rod);
            const lastSelectedVertebraItem = getLastSelectedVertebraItem(rod);

            // Use filtered dropdown items
            const firstItems = getFirstDropdownItems(rod);
            const firstVertebra =
              rod.pendingFirstVertebra ||
              (selectedRange.length > 0 ? selectedRange[0] : null);
            const secondItems = getSecondDropdownItems(rod, firstVertebra);
            const hasFirstSelected = firstVertebra && firstVertebra !== "-";

            // Check side availability based on range
            const { leftAvailable, rightAvailable } = getSideAvailability(
              selectedRange,
              rod.id
            );

            return (
              <div
                className="AlignPopupParameterSelector-range-row"
                key={rod.id}
              >
                <LabeledRangeSelect
                  label=""
                  firstItems={firstItems}
                  secondItems={secondItems}
                  firstSelectedItem={selectedVertebraItem}
                  secondSelectedItem={lastSelectedVertebraItem}
                  onChangeFirst={item => handleFirstVertebraChange(rod, item)}
                  onChangeSecond={item => handleSecondVertebraChange(rod, item)}
                  hasOnlyOneItem={false}
                  secondDisabled={!hasFirstSelected}
                />
                <div className="AlignPopupParameterSelector-rod-side-toggle-group">
                  <IconButton
                    icon={
                      isLeftRodEnabled
                        ? IconsEnum.RODLEFTENABLED
                        : IconsEnum.RODLEFTDISABLED
                    }
                    iconSize={IconSize.MEDIUM}
                    size={ButtonSize.DEFAULT}
                    isLightBlue={isLeftRodEnabled}
                    onClick={() => handleLeftRodToggle(rod)}
                    disabled={selectedRange.length === 0 || !leftAvailable}
                    className="AlignPopupParameterSelector-rod-side-toggle"
                    title={t("436")}
                    noBgColor
                  />
                  <IconButton
                    icon={
                      isRightRodEnabled
                        ? IconsEnum.RODRIGHTENABLED
                        : IconsEnum.RODRIGHTDISABLED
                    }
                    iconSize={IconSize.MEDIUM}
                    size={ButtonSize.DEFAULT}
                    isLightBlue={isRightRodEnabled}
                    onClick={() => handleRightRodToggle(rod)}
                    disabled={selectedRange.length === 0 || !rightAvailable}
                    className="AlignPopupParameterSelector-rod-side-toggle"
                    title={t("437")}
                    noBgColor
                  />
                  {rods.length > 1 && (
                    <IconButton
                      icon={IconsEnum.CLOSE}
                      iconSize={IconSize.MEDIUM}
                      size={ButtonSize.DEFAULT}
                      onClick={() => onRemoveRod(rod.id)}
                      className="AlignPopupParameterSelector-rod-side-toggle"
                      tooltipContent={"Remove rod"}
                      noBgColor
                    />
                  )}
                </div>
              </div>
            );
          })}
          <div className="AlignPopupParameterSelector-add-rod-section">
            <label className="AlignPopupParameterSelector-label App-text-medium">
              Add
            </label>
            <IconButton
              icon={IconsEnum.ADD}
              iconSize={IconSize.MEDIUM}
              size={ButtonSize.DEFAULT}
              onClick={onAddRod}
              className="AlignPopupParameterSelector-add-rod-button"
              tooltipContent={"Add another rod"}
              noBgColor
            />
          </div>
          <div />
        </div>

        {/* Reciprocal Change Section */}
        <div className="AlignPopupParameterSelector-controls-wrapper">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <label className="AlignPopupParameterSelector-label App-text-large">
              Reciprocal Change Simulation:
            </label>
            <IconButton
              icon={IconsEnum.INFO}
              title="Thoracic reciprocal change simulation based on spine flexibility"
              className="AlignPopupParameterSelector-info-button"
              onClick={e => e.stopPropagation()}
              isCircle
              size={ButtonSize.MICRO}
              isLightBlue={false}
              tooltipOrientation={TooltipOrientation.Right}
            />
          </div>

          <div className="AlignPopupParameterSelector-subtitle App-text-medium">
            Select the type of spine
          </div>

          <RadioButtons
            options={[
              SpineRigidityType.RIGID,
              SpineRigidityType.STIFF,
              SpineRigidityType.FLEXIBLE,
            ]}
            selectedOption={selectedSpineType}
            onChange={e => onSpineTypeChange(e.target.value)}
            name="spineType"
            displayName=""
            disabled={!canApplyReciprocalChange}
            className="radio-buttons-compact"
          />
        </div>

        <div className="AlignPopupParameterSelector-separator" />

        {/* Rod Section */}
        <div className="AlignPopupParameterSelector-moreoptions-container">
          <div className="AlignPopupParameterSelector-rod-header-row">
            <div className="AlignPopupParameterSelector-subtitle">
              <strong>Rod</strong>
            </div>
            <div className="AlignPopupParameterSelector-manufacturer-label">
              Patient Specific
            </div>
          </div>

          <div className="AlignPopupParameterSelector-manufacturer-row">
            <label className="AlignPopupParameterSelector-label App-text-large">
              Manufacturer
            </label>
            <div className="AlignPopupParameterSelector-manufacturer-label">
              MDsim
            </div>
          </div>

          {rods.map((rod, index) => {
            const expanded = isRodExpanded(rod.id);
            const hasLeft = (rod.selectedLeftScrews || []).length >= 2;
            const hasRight = (rod.selectedRightScrews || []).length >= 2;
            const leftLength = getRodLengthCalculated(rod, "left");
            const rightLength = getRodLengthCalculated(rod, "right");
            const sameLengths = leftLength === rightLength;

            // Don't render characteristics if rod has no valid selection
            if (!hasLeft && !hasRight) {
              return null;
            }

            return (
              <div
                key={`${rod.id}-characteristics`}
                className="AlignPopupParameterSelector-moreoptions-header"
              >
                <div onClick={() => toggleRodExpanded(rod.id)}>
                  <span className="App-text-medium">
                    {getRodCharacteristicsLabel(rod)}
                  </span>
                  <IconButton
                    icon={expanded ? IconsEnum.ARROWUP : IconsEnum.ARROWDOWN}
                    size="small"
                    isCircle={false}
                    noBgColor={true}
                    title={expanded ? t("434") : t("433")}
                  />
                </div>

                <div
                  className={`AlignPopupParameterSelector-expandable-area ${
                    expanded ? "expanded" : ""
                  }`}
                >
                  <LabeledSelect
                    label="Diameter"
                    items={diameterItems}
                    selectedItem={getSelectedDiameterItem(rod)}
                    onChange={item => handleDiameterChange(rod.id, item)}
                  />

                  <LabeledSelect
                    label="Material"
                    items={materialItems}
                    selectedItem={materialItems.find(
                      item => item.value === rod.rodMaterial
                    )}
                    onChange={item => {
                      const value = item.target ? item.target.value : item.value;
                      onUpdateRod(rod.id, { rodMaterial: value });
                    }}
                  />

                  {/* Show single length if both sides same, or separate lengths per side */}
                  {(hasLeft && hasRight && sameLengths) ||
                  (!hasLeft && !hasRight) ? (
                    <div className="AlignPopupParameterSelector-parameter-row">
                      <span className="AlignPopupParameterSelector-label App-text-large">
                        Length
                      </span>
                      <div className="AlignPopupParameterSelector-length-label">
                        {getRodLengthCalculated(rod)}
                      </div>
                    </div>
                  ) : (
                    <>
                      {hasLeft && (
                        <div className="AlignPopupParameterSelector-parameter-row">
                          <span className="AlignPopupParameterSelector-label App-text-large">
                            Length (L)
                          </span>
                          <div className="AlignPopupParameterSelector-length-label">
                            {leftLength}
                          </div>
                        </div>
                      )}
                      {hasRight && (
                        <div className="AlignPopupParameterSelector-parameter-row">
                          <span className="AlignPopupParameterSelector-label App-text-large">
                            Length (R)
                          </span>
                          <div className="AlignPopupParameterSelector-length-label">
                            {rightLength}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="AlignPopupParameterSelector-parameter-row">
                    <div className="AlignPopupParameterSelector-label-with-info">
                      <span className="AlignPopupParameterSelector-label App-text-large">
                        Extra Length
                      </span>
                      <IconButton
                        icon="Info"
                        title="Additional length beyond calculated rod length"
                        className="AlignPopupParameterSelector-info-button"
                        onClick={e => e.stopPropagation()}
                        isCircle
                        size={ButtonSize.MICRO}
                        isLightBlue={false}
                        tooltipOrientation={TooltipOrientation.Right}
                      />
                    </div>
                    <div className="AlignPopupParameterSelector-extra-length-controls">
                      <MeasurementDisplay
                        value={rod.overhangLength ?? 0}
                        unit="mm"
                      />
                      <IncrementDecrementButtons
                        onIncrement={() => handleExtraLengthIncrement(rod)}
                        onDecrement={() => handleExtraLengthDecrement(rod)}
                        onIncrementEnd={handleExtraLengthIncrementEnd}
                        onDecrementEnd={handleExtraLengthDecrementEnd}
                        disabled={false}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="AlignPopupParameterSelector-separator" />

        {/* Screw Section */}
        <div className="AlignPopupParameterSelector-moreoptions-container">
          <div className="AlignPopupParameterSelector-screw-header-row">
            <div className="AlignPopupParameterSelector-subtitle">
              <strong>Screws</strong>
            </div>
          </div>

          <LabeledSelect
            label="Material"
            items={materialItems}
            selectedItem={
              materialItems.find(item => item.value === screwMaterialValue) ||
              materialItems[0]
            }
            onChange={item => {
              const value = item.target ? item.target.value : item.value;
              rods.forEach(r => onUpdateRod(r.id, { screwMaterial: value }));
            }}
          />

          {screwSummary.length !== 0 && (
            <>
              <div className="AlignPopupParameterSelector-screw-size-group">
                <div className="AlignPopupParameterSelector-section-label">
                  <span className="AlignPopupParameterSelector-label App-text-large">
                    Size (diameter - length)
                  </span>
                </div>

                <div className="AlignPopupParameterSelector-screw-sizes-list">
                  {screwSummary.map(item => (
                    <div
                      key={item.label}
                      className="AlignPopupParameterSelector-screw-size-item"
                    >
                      <span className="screw-size-label">⌀ {item.label}</span>
                      <span className="screw-size-count">x{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <span className="AlignPopupParameterSelector-screw-disclaimer-text">
                Screw selection is clinician-defined and recorded for reporting
                only. It is not verified by the software or used in clinical or
                biomechanical calculations
              </span>
            </>
          )}
        </div>
      </div>

      <div className="AlignPopupParameterSelector-button-wrapper">
        <Button
          value={isConfirming ? "Confirming..." : "Confirm"}
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
        />
      </div>
    </div>
  );
};

export default Rod;
